import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Description,
  CheckCircle,
  CloudDownload,
  Close,
  CheckBox,
  CheckBoxOutlineBlank,
  Sync,
  Error as ErrorIcon,
  FolderCopy,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import { ixcImportService } from '../../../services/ixcImportService';

const STORAGE_KEY_BASE_URL = 'sysup_ixc_import_baseUrl';
const STORAGE_KEY_TOKEN = 'sysup_ixc_import_token';

interface ImportIxcDocumentosModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = ['Configuração', 'Selecionar Clientes', 'Importar Arquivos'];

export const ImportIxcDocumentosModal: React.FC<ImportIxcDocumentosModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ativos' | 'inativos' | 'todos'>('ativos');
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'nome', sort: 'asc' }]);

  // Carregar credenciais salvas
  useEffect(() => {
    if (open) {
      const savedUrl = localStorage.getItem(STORAGE_KEY_BASE_URL);
      const savedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
      if (savedUrl) setBaseUrl(savedUrl);
      if (savedToken) setToken(savedToken);
    }
  }, [open]);

  // Salvar credenciais ao alterar
  useEffect(() => {
    if (baseUrl.trim()) localStorage.setItem(STORAGE_KEY_BASE_URL, baseUrl.trim());
    if (token) localStorage.setItem(STORAGE_KEY_TOKEN, token);
  }, [baseUrl, token]);

  const handleBuscarClientes = async () => {
    if (!baseUrl || !token) {
      toast.error('Preencha a URL e o Token da API');
      return;
    }
    setLoading(true);
    try {
      const data = await ixcImportService.listClientesComImportacao(statusFilter);
      setClientes(data);
      setSelectedIds(new Set());
      setActiveStep(1);
    } catch (error: any) {
      console.error('Erro ao buscar clientes:', error);
      toast.error(error.message || 'Erro ao buscar clientes no banco de dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clientes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(clientes.map(c => c.id)));
    }
  };

  const handleIniciarImportacao = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Selecione ao menos um cliente');
      return;
    }
    setActiveStep(2);
    setImporting(true);
    setResults([]);

    const idsToImport = Array.from(selectedIds);
    let totalArquivos = 0;

    try {
      for (const clienteId of idsToImport) {
        const cliente = clientes.find(c => c.id === clienteId);
        try {
          const result = await ixcImportService.importarDocumentosClienteUnico({
            baseUrl: baseUrl.trim(),
            token: token.trim(),
            clienteId,
          });

          setResults((prev) => [...prev, {
            ...result,
            nome: cliente?.nome || `ID ${clienteId}`
          }]);
          
          if (result.status === 'sucesso') {
            totalArquivos += result.arquivos_importados;
          }
        } catch (err: any) {
          setResults((prev) => [...prev, {
            status: 'erro',
            erro: err.message || 'Falha na requisição',
            nome: cliente?.nome || `ID ${clienteId}`
          }]);
        }
      }

      toast.success(`Importação concluída. Total de arquivos: ${totalArquivos}`);
      onSuccess?.();
    } catch (error: any) {
      console.error('Erro na importação:', error);
      toast.error('Ocorreu um erro inesperado durante a importação.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (importing) return;
    setActiveStep(0);
    setClientes([]);
    setSelectedIds(new Set());
    setResults([]);
    onClose();
  };

  const rows = useMemo(() => {
    return clientes.map((c) => ({
      ...c,
      rowId: c.id,
    }));
  }, [clientes]);

  const renderStep0 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure o acesso à API do IXC para baixar os documentos (arquivos) dos clientes.
      </Typography>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="URL da API IXC"
          placeholder="https://provedor.ixcsoft.com.br"
          fullWidth
          size="small"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
        <TextField
          label="Token da API (Basic Auth)"
          placeholder="id:token"
          fullWidth
          size="small"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          type="password"
        />
        
        <FormControl fullWidth size="small">
          <InputLabel>Status dos Clientes (no Sysup)</InputLabel>
          <Select
            value={statusFilter}
            label="Status dos Clientes (no Sysup)"
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <MenuItem value="ativos">Somente Ativos</MenuItem>
            <MenuItem value="inativos">Somente Inativos</MenuItem>
            <MenuItem value="todos">Todos</MenuItem>
          </Select>
        </FormControl>

        <Button 
          variant="contained" 
          onClick={handleBuscarClientes} 
          disabled={loading}
          startIcon={loading ? <Sync className="animate-spin" /> : <Sync />}
        >
          {loading ? 'Carregando...' : 'Carregar Clientes do Sistema'}
        </Button>
      </Box>
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Selecione os clientes que deseja baixar os documentos do IXC. 
      </Typography>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={rows}
          getRowId={(row) => row.rowId}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <IconButton size="small" onClick={toggleSelectAll}>
                  {selectedIds.size === clientes.length && clientes.length > 0 ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton size="small" onClick={() => toggleSelect(params.row.id)}>
                  {selectedIds.has(params.row.id) ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
            },
            { field: 'id_importacao', headerName: 'ID IXC', width: 130 },
            { field: 'nome', headerName: 'Cliente', flex: 1 },
            { field: 'cpf_cnpj', headerName: 'CPF/CNPJ', width: 150 },
          ]}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Baixando documentos dos clientes selecionados...
      </Typography>
      {importing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={(results.length / selectedIds.size) * 100} 
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            Processando {results.length} de {selectedIds.size}...
          </Typography>
        </Box>
      )}
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {results.map((r, i) => (
          <Card key={i} sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight="bold">{r.nome}</Typography>
                {r.status === 'sucesso' ? (
                  <Chip 
                    label={`${r.arquivos_importados} arquivo(s)`} 
                    color="success" 
                    size="small" 
                    icon={<CheckCircle />}
                  />
                ) : (
                  <Chip 
                    label="Erro" 
                    color="error" 
                    size="small" 
                    icon={<ErrorIcon />}
                  />
                )}
              </Box>
              {r.erro && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                  {r.erro}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderCopy color="primary" />
          Importar Documentos (Arquivos) IXC
        </Box>
        <IconButton onClick={handleClose} disabled={importing} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-spin { animation: spin 1s linear infinite; }
        `}</style>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {activeStep === 2 && !importing ? 'Fechar' : 'Cancelar'}
        </Button>
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Voltar</Button>
            <Button
              onClick={handleIniciarImportacao}
              variant="contained"
              startIcon={<CloudDownload />}
            >
              Importar de {selectedIds.size} Cliente(s)
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
